import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Key, Eye, EyeOff } from 'lucide-react';
import { API_URL } from '../utils/apiUrl';
import './MemberRecover.css';

/**
 * MemberRecover - Account recovery flow
 * 
 * Two modes:
 * 1. Request recovery - Enter email to receive recovery link
 * 2. Confirm recovery - Set new password (when accessed with token)
 */
export const MemberRecover = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Request mode state
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Confirm mode state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  const handleRequestRecovery = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/clubhouse/account/recover/request`, {
        email: email.toLowerCase().trim()
      });
      setRequestSent(true);
    } catch (err) {
      // Always show success message to prevent account enumeration
      setRequestSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRecovery = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/clubhouse/account/recover/confirm`, {
        token: token,
        new_password: newPassword
      });
      setRecoverySuccess(true);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to recover account. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/clubhouse/login');
  };

  // Token mode - Confirm recovery with new password
  if (token) {
    if (recoverySuccess) {
      return (
        <div className="member-recover-page">
          <div className="recover-container">
            <div className="recover-icon success">
              <CheckCircle size={48} />
            </div>
            <h1>Account Recovered!</h1>
            <p className="recover-description">
              Your account has been restored. You can now log in with your new password.
            </p>
            <button className="btn-primary" onClick={handleLoginRedirect}>
              Log In to Clubhouse
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="member-recover-page">
        <div className="recover-container">
          <div className="recover-icon">
            <Key size={48} />
          </div>
          <h1>Set New Password</h1>
          <p className="recover-description">
            Create a new password for your recovered account.
          </p>

          {error && (
            <div className="recover-error">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <form onSubmit={handleConfirmRecovery} className="recover-form">
            <div className="form-group">
              <label>New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <span className="input-hint">At least 8 characters</span>
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Recovering...' : 'Recover My Account'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Default mode - Request recovery link
  if (requestSent) {
    return (
      <div className="member-recover-page">
        <div className="recover-container">
          <div className="recover-icon success">
            <Mail size={48} />
          </div>
          <h1>Check Your Email</h1>
          <p className="recover-description">
            If an account with that email was deleted, you&apos;ll receive a recovery link shortly.
          </p>
          <p className="recover-note">
            The link will expire in 60 minutes. Check your spam folder if you don&apos;t see it.
          </p>
          <div className="recover-actions">
            <button className="btn-secondary" onClick={() => setRequestSent(false)}>
              Try Another Email
            </button>
            <button className="btn-text" onClick={() => navigate('/clubhouse/login')}>
              <ArrowLeft size={16} /> Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="member-recover-page">
      <div className="recover-container">
        <div className="recover-icon">
          <Mail size={48} />
        </div>
        <h1>Recover Your Account</h1>
        <p className="recover-description">
          Enter the email address associated with your deleted account. We&apos;ll send you a link to restore it.
        </p>

        {error && (
          <div className="recover-error">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        <form onSubmit={handleRequestRecovery} className="recover-form">
          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Sending...' : 'Send Recovery Link'}
          </button>
        </form>

        <button className="btn-text" onClick={() => navigate('/clubhouse/login')}>
          <ArrowLeft size={16} /> Back to Login
        </button>
      </div>
    </div>
  );
};

export default MemberRecover;
